import React, { ComponentProps, useContext, useMemo, useState } from 'react';
import { default as Form } from '../../components/connectedForm';
import QueryDropdown from '../../components/connectedForm/QueryDropdown';
import normalizeVariables from '../../graphql/schema/normalizeVariables';
import { SuccessButton } from '../../components/connectedForm/Button';
import PriceListWorkspace from '../PriceListWorkspace';
import WorkspaceContext from '../../components/Context/WorkspaceContext';
import {
  CustomerPriceListVatInclusive,
  ICustomer,
  ICustomerPriceListReportMutationResult,
  ICustomerPriceListReportMutationVariables,
  Maybe,
} from '../../graphql/schema/p4types';
import Dropdown from '../../components/connectedForm/Dropdown';
import fieldMetaData from '../../graphql/schema/fieldMetaData';
import DocumentModal2 from '../../components/modals/DocumentModal2';
import getClientMutationId from '../../utils/getClientMutationId';
import { useBackgroundMutation } from '../../utils/BackgroundJobs';
import { onDocumentComplete } from '../../utils/DocumentUtils';
import { useActionCriteria } from '../../utils/useActionProps';
import { useDispatch } from 'react-redux';
import Customer from '../../graphql/entities/customer/Customer';
import CustomerPriceList from '../../graphql/entities/customer/CustomerPriceList';
import HeaderBlock from '../../components/connectedForm/HeaderBlock';
import { useOnProcessPreview } from '../../components/modals/EmailPrintPreviewModal';

const CUSTOMER_PRICE_LIST_FIELDNAMES = [
  'product.reference',
  'product.description',
  'product.latestCostPrice',
  'product.costUnitDescription',
  'customerPricePotential.nettPrice',
  'product.sellingUnitDescription',
  'product.vatProductCategory.description',
  'pricePotentialList',
  'product.mainSupplierDetails.supplier.reference',
];

const CustomWorkspaceHeader = ({
  customer,
  setCustomer,
  customerPriceListVatInclusive,
  setCustomerPriceListVatInclusive,
}: {
  customer?: ICustomer | null;
  setCustomer: Function;
  customerPriceListVatInclusive?: CustomerPriceListVatInclusive;
  setCustomerPriceListVatInclusive?: Function | null;
}) => {
  const { selectionState, workspaceSelection } = useContext(WorkspaceContext);
  const actionCriteria = useActionCriteria();
  const dispatch = useDispatch();
  const onRowSelect = workspaceSelection.onRowSelect;
  const [printIsOpen, setPrintIsOpen] = React.useState(false);

  const onCustomerChange = (value: ICustomer) => {
    setCustomer(value);
    onRowSelect([], [], false);
  };

  const printButton = (
    <SuccessButton
      disabled={!customer}
      onClick={() => {
        setPrintIsOpen(true);
      }}
    >
      Print
    </SuccessButton>
  );

  const onProcessPreview = useOnProcessPreview();
  const [generateCustomerPriceReport] = useBackgroundMutation<
    ICustomerPriceListReportMutationResult,
    ICustomerPriceListReportMutationVariables
  >(CustomerPriceList.generation, {
    onCompleteJob: (job) => {
      onDocumentComplete?.({ documentName: 'Customer Price Report', dispatch, job, onProcessPreview });
    },
  });

  return (
    <>
      <DocumentModal2
        modalOpen={printIsOpen}
        entity={CustomerPriceList}
        title={'Print Customer Price List'}
        modalWrapper={true}
        multiValue={false}
        count={selectionState.count}
        useCustomerDefaults={false}
        blockNavigation={true}
        disableIndividual={true}
        onClose={() => {
          setPrintIsOpen(false);
        }}
        onPrintClick={({ reportParameters }) => {
          generateCustomerPriceReport({
            variables: {
              input: {
                actionEntities: normalizeVariables(actionCriteria.actionEntities, ['priceOriginDescription']),
                clientMutationId: getClientMutationId(),
                reportParameters: normalizeVariables(reportParameters),
                customer: normalizeVariables(customer),
                filters: actionCriteria.filters,
                include: actionCriteria.include,
              },
            },
          });
        }}
      />
      <HeaderBlock name={CustomerPriceList.title} extraBtns={printButton} entity={CustomerPriceList} />
      <Form.Form>
        <Form.Row>
          <Form.Fieldset cols={1 / 2}>
            <Form.Legend label="General" />
            <QueryDropdown
              {...Customer.dropdown}
              includeLabel={false}
              value={customer}
              onChange={onCustomerChange}
              readOnly={false}
              clearable={false}
              data-testid={'customer-dropdown'}
            />
            <Dropdown
              label="VAT Inclusive"
              onChange={setCustomerPriceListVatInclusive}
              enum={true}
              labelCols={4 / 12}
              inputCols={8 / 12}
              options={fieldMetaData.CustomerPriceListVatInclusive.enumValues}
              value={customerPriceListVatInclusive}
            />
          </Form.Fieldset>
        </Form.Row>
      </Form.Form>
    </>
  );
};

const PriceListWorkspaceWrapper = (props: Omit<ComponentProps<typeof PriceListWorkspace>, 'fieldNames' | 'entity'>) => {
  const fieldNames = useMemo(() => {
    return [...CUSTOMER_PRICE_LIST_FIELDNAMES, ...(props?.additionalFieldNames || [])];
  }, [props?.additionalFieldNames]);

  return <PriceListWorkspace fieldNames={fieldNames} entity={CustomerPriceList} {...props} />;
};

export default (props: ComponentProps<typeof PriceListWorkspaceWrapper>) => {
  const [customer, setCustomer] = useState<Maybe<ICustomer>>(null);
  const [customerPriceListVatInclusive, setCustomerPriceListVatInclusive] = useState(
    CustomerPriceListVatInclusive.CustomerDefault
  );

  if (props.embeddedView) {
    // If we have an embedded prop, return the Workspace directly.
    return <PriceListWorkspaceWrapper {...props} />;
  }

  // Otherwise, we're displaying as a main screen, so add the custom header for configuring the Customer.
  // (We still pass embedded to the actual Workspace because we want some common side effects of using that prop).
  return (
    <PriceListWorkspaceWrapper
      embeddedView={true}
      viewScreenName="/customers/CustomerPriceList/Workspace"
      additionalVariables={{
        customer: normalizeVariables(customer),
        customerPriceListVatInclusive: customerPriceListVatInclusive,
      }}
      enableRowSelect={true}
      customHeader={
        <CustomWorkspaceHeader
          customer={customer}
          setCustomer={setCustomer}
          customerPriceListVatInclusive={customerPriceListVatInclusive}
          setCustomerPriceListVatInclusive={setCustomerPriceListVatInclusive}
        />
      }
      ignoreContainerWidth={true}
    />
  );
};
